import { ApolloProvider } from "@apollo/client";
import { client } from "./utils/apollo";
import loadable from "@loadable/component";
import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { RequireAuth } from "./components/auth/RequireAuth";
import { StoreCheckout } from "./components/checkout/Checkout.types";
import { useContentfulContext } from "./context/ContentfulContext";
import ShoppingCartContextProvider from "./context/ShoppingCartContext";
import SignedInContextProvider from "./context/SignedInContext";
import { useFeatureFlag } from "./components/common/FeatureFlag";
import {
  CREATE_PASSWORD_URL,
  SIGNUP_ADDRESS_URL,
  SIGNUP_CONTACTINFO_URL,
  SIGNUP_LEGALNAME_URL,
  SIGNUP_SCHEDULE_URL,
  SIGN_UP_ABOUT_YOUR_DATA_URL,
  SIGN_UP_PAYMENT_URL,
  SIGN_UP_REFERRALS_URL,
  SUBSCRIPTION_PLANS_URL,
} from "./components/signup/SignUp";
import {
  SIGN_UP_V2_ABOUT_YOUR_DATA_URL,
  SIGN_UP_V2_CREATE_PASSWORD_URL,
  SIGN_UP_V2_INDEX_ROUTE_URL,
  SIGN_UP_V2_LEGALNAME_URL,
  SIGN_UP_V2_PAYMENT_URL,
  SIGN_UP_V2_PHONE_NUMBER_URL,
  SIGN_UP_V2_REFERRALS_URL,
  SIGN_UP_V2_SUBSCRIPTION_PLANS_URL,
  SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL,
  SIGN_UP_V2_IMPORTANT_INFORMATION_URL,
  SIGN_UP_V2_CREDIT_KARMA_URL,
  SIGN_UP_V2_CLEARSCORE_URL,
  SIGN_UP_V2_TOTALLY_MONEY_URL,
} from "./components/cassetteUI/signupFlow/signup/SignUpV2";
import { GoogleTagManagerScript } from "./components/common/GoogleTagManagerScript";
import { ExternalRedirect } from "./components/common/ExternalRedirect";
import AuthService from "./components/auth/AuthService";
import { TrackAnalytic } from "./components/common/TrackAnalytic";
import { ScoreApply } from "./components/score/ScoreApply";
import { SignupLikeRoutesLayout } from "./components/cassetteUI/layout/SignupLikeRoutesLayout";
import { EAggregators } from "utils/aggregators";
import { useAggregatorTracking } from "hooks/useAggregatorTracking";
import { useQuery } from "hooks/useQuery";
import { MFA } from "components/auth/2FA";
import { RequireStoreAccess } from "./components/cassetteUI/store/RequireStoreAccess";
import { isNativeWebView, NATIVE_TOKEN_GRACE_MS } from "./utils/webview";

const LaunchApp = loadable(() => import(/* webpackChunkName: "LaunchApp" */ "./components/LaunchApp"), {
  resolveComponent: (components) => components.LaunchApp,
}) as any;

const Clearscore = loadable(() => import(/* webpackChunkName: "Clearscore" */ "./components/aggregators/Clearscore"), {
  resolveComponent: (components) => components.Clearscore,
}) as any;

const CreditKarma = loadable(
  () => import(/* webpackChunkName: "CreditKarma" */ "./components/aggregators/CreditKarma"),
  {
    resolveComponent: (components) => components.CreditKarma,
  }
) as any;

const TotallyMoney = loadable(
  () => import(/* webpackChunkName: "TotallyMoney" */ "./components/aggregators/TotallyMoney"),
  {
    resolveComponent: (components) => components.TotallyMoney,
  }
) as any;

const MoneySupermarket = loadable(
  () => import(/* webpackChunkName: "MoneySupermarket" */ "./components/aggregators/MoneySupermarket"),
  {
    resolveComponent: (components) => components.MoneySupermarket,
  }
) as any;

const Vanquis = loadable(() => import(/* webpackChunkName: "Vanquis" */ "./components/aggregators/Vanquis"), {
  resolveComponent: (components) => components.Vanquis,
}) as any;

const Monevo = loadable(() => import(/* webpackChunkName: "Monevo" */ "./components/aggregators/Monevo"), {
  resolveComponent: (components) => components.Monevo,
}) as any;

const Tuk = loadable(() => import(/* webpackChunkName: "Tuk" */ "./components/aggregators/Tuk"), {
  resolveComponent: (components) => components.Tuk,
}) as any;

const Creditec = loadable(() => import(/* webpackChunkName: "Creditec" */ "./components/aggregators/Creditec"), {
  resolveComponent: (components) => components.Creditec,
}) as any;

const Influencer = loadable(() => import(/* webpackChunkName: "Influencer" */ "./components/aggregators/Influencer"), {
  resolveComponent: (components) => components.Influencer,
}) as any;

const SignUpV2 = loadable(
  () => import(/* webpackChunkName: "SignUpV2Uk" */ "./components/cassetteUI/signupFlow/signup/SignUpV2"),
  {
    resolveComponent: (components) => components.SignUpV2,
  }
) as any;

const SignUp = loadable(() => import(/* webpackChunkName: "SignUp" */ "./components/signup/SignUp"), {
  resolveComponent: (components) => components.SignUp,
}) as any;

const PeriodicStatements = loadable(
  () => import(/* webpackChunkName: "PeriodicStatements" */ "./components/periodicStatements/PeriodicStatements"),
  {
    resolveComponent: (components) => components.PeriodicStatements,
  }
) as any;

const Cart = loadable(() => import(/* webpackChunkName: "Cart" */ "./components/cassetteUI/commerce/CartPage"), {
  resolveComponent: (components) => components.CartPage,
}) as any;

const Orders = loadable(() => import(/* webpackChunkName: "Orders" */ "./components/store/orders/Orders"), {
  resolveComponent: (components) => components.Orders,
}) as any;

const CartTimeout = loadable(() => import(/* webpackChunkName: "CartTimeout" */ "./components/cart/CartTimeout"), {
  resolveComponent: (components) => components.CartTimeout,
}) as any;

const ShoppingCartFailure = loadable(
  () => import(/* webpackChunkName: "ShoppingCartFailure" */ "./components/checkout/cart/ShoppingCartFailure")
) as any;

const UnauthorizedRoutesLayout = loadable(
  () =>
    import(
      /* webpackChunkName: "UnauthorizedRoutesLayoutUk" */ "./components/cassetteUI/layout/UnauthorizedRoutesLayout/UnauthorizedRoutesLayout"
    ),
  {
    resolveComponent: (components) => components.UnauthorizedRoutesLayout,
  }
) as any;
const UserCookieConsent = loadable(
  () => import(/* webpackChunkName: "UserCookieConsent" */ "./components/common/UserCookieConsent"),
  {
    resolveComponent: (components) => components.UserCookieConsent,
  }
) as any;

const VideoList = loadable(() => import(/* webpackChunkName: "VideoList" */ "./components/video/VideoList"), {
  resolveComponent: (components) => components.VideoList,
}) as any;

const Layout = loadable(() => import(/* webpackChunkName: "Layout" */ "./components/cassetteUI/layout/Layout"), {
  resolveComponent: (components) => components.Layout,
}) as any;

const Logout = loadable(() => import(/* webpackChunkName: "Logout" */ "./components/auth/Logout"), {
  resolveComponent: (components) => components.Logout,
}) as any;

/* `ResetPasswordUk`, not `ResetPassword`: `AppWrapper` gives that name to the
   US page, and a shared chunk name merges the two — which shipped the US
   stylesheet here, where its `#reset-password-container` rules outrank the
   step card. Same reason as `CreateNewPassword` below. */
const ResetPassword = loadable(
  () =>
    import(/* webpackChunkName: "ResetPasswordUk" */ "./components/cassetteUI/signupFlow/forgotPassword/ResetPassword")
) as any;

/* The cassette step card, not `auth/change-password/ResetPassword` — that one
   is the US layout and is still what `AppWrapper` renders.

   The chunk name has to differ from the US one: webpack merges everything
   sharing a `webpackChunkName` into a single chunk, so while both were called
   "ChangePassword" this route also shipped the US stylesheet — whose rules
   hang off `#change-password-container` and outrank the step card at id
   specificity. */
const ChangePassword = loadable(
  () =>
    import(
      /* webpackChunkName: "CreateNewPassword" */ "./components/cassetteUI/signupFlow/forgotPassword/CreateNewPassword"
    ),
  {
    resolveComponent: (components) => components.CreateNewPassword,
  }
) as any;

const Error404 = loadable(() => import(/* webpackChunkName: "Error404" */ "./components/cassetteUI/error/Error404"), {
  resolveComponent: (components) => components.Error404,
}) as any;

const NotFound = loadable(
  () => import(/* webpackChunkName: "NotFound" */ "./components/cassetteUI/signupFlow/notFound/NotFound"),
  {
    resolveComponent: (components) => components.NotFound,
  }
) as any;

const Settings = loadable(
  () => import(/* webpackChunkName: "Settings" */ "./components/cassettePages/settings/Settings"),
  {
    resolveComponent: (components) => components.Settings,
  }
) as any;

const Home = loadable(() => import(/* webpackChunkName: "Home" */ "./components/cassettePages/home/Home"), {
  resolveComponent: (components) => components.Home,
}) as any;

const Store = loadable(() => import(/* webpackChunkName: "Store" */ "./components/cassetteUI/store/Store"), {
  resolveComponent: (components) => components.Store,
}) as any;

const Checkout = loadable(
  () => import(/* webpackChunkName: "Checkout" */ "./components/cassetteUI/commerce/CheckoutPage"),
  {
    resolveComponent: (components) => components.CheckoutPage,
  }
) as any;

const ProductList = loadable(
  () => import(/* webpackChunkName: "ProductList" */ "./components/cassetteUI/store/prodcut-list/ProductList"),
  {
    resolveComponent: (components) => components.ProductList,
  }
) as any;

const ProductDetails = loadable(
  () => import(/* webpackChunkName: "ProductDetails" */ "./components/cassetteUI/store/product-details/ProductDetails"),
  {
    resolveComponent: (components) => components.ProductDetails,
  }
) as any;

const OurImpact = loadable(() => import(/* webpackChunkName: "OurImpact" */ "./components/ourImpact/OurImpact"), {
  resolveComponent: (components) => components.OurImpact,
}) as React.ComponentType<any>;

const PaymentFailed = loadable(
  () => import(/* webpackChunkName: "PaymentFailed" */ "./components/store/upcoming-payments/PaymentFailed")
) as any;

const AllStoreCategories = loadable(
  () => import(/* webpackChunkName: "AllStoreCategories" */ "./components/cassetteUI/store/AllStoreCategories"),
  {
    resolveComponent: (components) => components.AllStoreCategories,
  }
) as any;

const Login = loadable(
  () => import(/* webpackChunkName: "LoginUk" */ "./components/cassetteUI/signupFlow/signin/Login"),
  {
    resolveComponent: (components) => components.Login,
  }
) as any;

const Membership = loadable(
  () => import(/* webpackChunkName: "Membership" */ "./components/cassettePages/membership/Membership"),
  {
    resolveComponent: (components) => components.Membership,
  }
) as any;
const SignUpDiscount = loadable(
  () => import(/* webpackChunkName: "SignUpDiscount" */ "./components/signup/signup-discount/SignUpDiscount"),
  {
    resolveComponent: (components) => components.SignUpDiscount,
  }
) as any;
interface IUseApp {
  headerClass: string | null;
  wrapperClass: string | null;
  bypassAuthCheck: boolean;
  displayBackButton: boolean;
  setHeaderClass: (headerClass: string | null) => void;
  setBypassAuthCheck: (bypassAuthCheck: boolean) => void;
  setWrapperClass: (wrapperClass: string | null) => void;
  setDisplayBackButton: (displayBackButton: boolean) => void;
  backButtonRoute: string | undefined;
  checkout: StoreCheckout | undefined;
  signUpRoute: JSX.Element | undefined;
  contentReady: boolean;
  isLoading: boolean;
  hideCookies: boolean;
  awaitingNativeSession: boolean;
}

export const SIGN_UP_FLOW_V2_ACTIVE_KEY = "signupFlowV2";
export const SIGN_UP_DISABLED_KEY = "signupDisabled";

const LOGIN_REDIRECT_ENABLED = process.env.REACT_APP_LOGIN_REDIRECT_ENABLED === "true";

/** The one signup, on the landing site. */
export const SIGNUP_SITE_URL = "https://www.getbits.app/signup/";

const useApp = (): IUseApp => {
  const [headerClass, setHeaderClass] = useState<string | null>(null);
  const [bypassAuthCheck, setBypassAuthCheck] = useState<boolean>(false);
  const [wrapperClass, setWrapperClass] = useState<string | null>(null);
  const [displayBackButton, setDisplayBackButton] = useState<boolean>(false);
  const [backButtonRoute, setBackButtonRoute] = useState<string | undefined>(undefined);
  const { pathname } = useLocation();
  const [checkout] = useState<StoreCheckout>();
  const [signUpRoute, setSignUpRoute] = useState<JSX.Element | undefined>();
  const { contentReady, content } = useContentfulContext();
  const isSignUpFlowV2Active = useFeatureFlag(SIGN_UP_FLOW_V2_ACTIVE_KEY);
  const isSignUpDisabled = useFeatureFlag(SIGN_UP_DISABLED_KEY);
  const navigate = useNavigate();
  const { displayImportantInformationPage }: any = content ?? { displayImportantInformationPage: false };
  const [aggregator, setAggregator] = useState<EAggregators | undefined>();
  const aggregatorMap: any = {};
  aggregatorMap[SIGN_UP_V2_TOTALLY_MONEY_URL] = EAggregators.TOTALLY_MONEY_STORE_CARD;
  aggregatorMap[SIGN_UP_V2_CREDIT_KARMA_URL] = EAggregators.CREDIT_KARMA_STORE_CARD;
  const { trackInit } = useAggregatorTracking();
  const [isLoading, setIsLoading] = useState(true);
  const [hideCookies, setHideCookies] = useState(false);

  /* Inside the app the native shell hands the session over after this bundle
     has already mounted. Without this the router renders the signed-out route
     first, so the user sees the login screen flash past before being moved on.
     Read synchronously so nothing paints in the meantime. */
  const [awaitingNativeSession, setAwaitingNativeSession] = useState<boolean>(
    () => isNativeWebView() && !AuthService.isAuthenticated() && !window.bitsAccessToken
  );
  const aggregatorUid = useQuery().get("uid") ?? undefined;
  /** The route the native session handover has already been acted on for. */
  const handledRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (![SIGN_UP_V2_TOTALLY_MONEY_URL, SIGN_UP_V2_CLEARSCORE_URL, SIGN_UP_V2_CREDIT_KARMA_URL].includes(pathname)) {
      setIsLoading(false);
      return;
    }
    //@ts-ignore
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobile =
      /windows phone/i.test(userAgent) ||
      /android/i.test(userAgent) ||
      //@ts-ignore
      (/iPhone|iPod|iPad/.test(userAgent) && !window.MSStream);
    trackInit(aggregatorMap[pathname], aggregatorUid)
      .then((link: string) => {
        if (isMobile) {
          window.location.href = link;
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(isMobile));
  }, []);

  const handleWebViewTokenChange = () => {
    const token: string = window?.bitsAccessToken ?? "";
    const route: string = window?.webViewRequestedRoute ?? "";
    /* Inside the app the shell has already asked for consent on the user's
       behalf, so the banner here is a second one over the top of the page. The
       shell says so explicitly where it knows to; where it does not, being in
       a webview at all is enough to go on. */
    const hideCookies: boolean = window?.hideCookies === "true" || isNativeWebView();
    setHideCookies(hideCookies);
    setAwaitingNativeSession(false);
    if (!token) {
      return;
    }
    AuthService.saveAuthToken(token);

    /* The store is the whole reason the webview gets opened from the app, and
       the dashboard is a screen the shell already has of its own — so that is
       where an unaddressed handover lands. A browser still starts at the
       dashboard. */
    const target = route || (isNativeWebView() ? "/store" : "/dashboard");

    /* The shell can hand the same session over more than once: Android re-runs
       its bootstrap when the webview is resumed, and returning to an already
       loaded page calls `setBitsAccessToken` again. Navigating every time
       pulled the user back to the landing route from wherever they had got
       to — off page two of the gift cards, and back through the store's own
       redirect — which is most of what "it keeps reloading" looked like on a
       phone. The first handover moves them; after that only a genuinely
       different requested route does. */
    if (handledRouteRef.current === target) {
      return;
    }
    handledRouteRef.current = target;
    navigate(target);
  };

  useEffect(() => {
    window.addEventListener("bitsAccessToken", handleWebViewTokenChange);
    if (window?.bitsAccessToken) {
      handleWebViewTokenChange();
    }
    /* A webview whose user really is signed out never calls back, so the gate
       has to open on its own or the app would never render at all. */
    const grace = window.setTimeout(() => setAwaitingNativeSession(false), NATIVE_TOKEN_GRACE_MS);
    return () => {
      window.clearTimeout(grace);
      window.removeEventListener("bitsAccessToken", handleWebViewTokenChange);
    };
  }, []);

  const getAggregatorRoute = () => {
    switch (aggregator) {
      case EAggregators.TOTALLY_MONEY:
        return SIGN_UP_V2_TOTALLY_MONEY_URL;
      case EAggregators.CLEARSCORE:
        return SIGN_UP_V2_CLEARSCORE_URL;
      case EAggregators.CREDIT_KARMA:
        return SIGN_UP_V2_CREDIT_KARMA_URL;
      default:
        return SIGN_UP_V2_INDEX_ROUTE_URL;
    }
  };
  const getBackButtonRouteV2 = (): string | undefined => {
    switch (pathname.toLowerCase()) {
      case SIGN_UP_V2_CREATE_PASSWORD_URL:
        return SIGN_UP_V2_PHONE_NUMBER_URL;
      case SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL:
        return SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL;
      case SIGN_UP_V2_LEGALNAME_URL:
        return SIGN_UP_V2_SUBSCRIPTION_PLANS_URL;
      case SIGN_UP_V2_PHONE_NUMBER_URL:
        return aggregator !== undefined ? getAggregatorRoute() : SIGN_UP_V2_INDEX_ROUTE_URL;
      case SIGN_UP_V2_REFERRALS_URL:
        return SIGN_UP_V2_LEGALNAME_URL;
      case SIGN_UP_V2_IMPORTANT_INFORMATION_URL:
        return SIGN_UP_V2_REFERRALS_URL;
      case SIGN_UP_V2_ABOUT_YOUR_DATA_URL:
        return displayImportantInformationPage ? SIGN_UP_V2_IMPORTANT_INFORMATION_URL : SIGN_UP_V2_REFERRALS_URL;
      case SIGN_UP_V2_PAYMENT_URL:
        return SIGN_UP_V2_ABOUT_YOUR_DATA_URL;

      default:
        return undefined;
    }
  };

  /**
   *  email
   *  phone
   *  password
   *  Legal name
   * Referrals
   * Important info
   * about your data
   * subscription plans
   * sign up v2 payment url
   *
   **/
  console.log("signUpRoute", signUpRoute);
  const getBackButtonRouteV1 = (): string | undefined => {
    switch (pathname.toLowerCase()) {
      case SIGNUP_ADDRESS_URL:
        return SIGNUP_LEGALNAME_URL;
      case SIGNUP_CONTACTINFO_URL:
        return SIGNUP_ADDRESS_URL;
      case CREATE_PASSWORD_URL:
        return SIGNUP_CONTACTINFO_URL;
      case SIGN_UP_REFERRALS_URL:
        return CREATE_PASSWORD_URL;
      case SIGN_UP_ABOUT_YOUR_DATA_URL:
        return SIGN_UP_REFERRALS_URL;
      case SUBSCRIPTION_PLANS_URL:
        return SIGN_UP_ABOUT_YOUR_DATA_URL;
      case SIGN_UP_PAYMENT_URL:
        return SUBSCRIPTION_PLANS_URL;
      case SIGNUP_SCHEDULE_URL:
        return SUBSCRIPTION_PLANS_URL;
      default:
        return undefined;
    }
  };

  const getBackButtonRoute = () => (isSignUpFlowV2Active ? getBackButtonRouteV2() : getBackButtonRouteV1());

  const getSignUpRoute = () => {
    if (isSignUpDisabled) {
      return <Navigate to="/login" replace />;
    }
    return isSignUpFlowV2Active ? (
      <SignUpV2
        setHeaderClass={setHeaderClass}
        setWrapperClass={setWrapperClass}
        setBypassAuthCheck={setBypassAuthCheck}
        setDisplayBackButton={setDisplayBackButton}
        setAggregator={setAggregator}
      />
    ) : (
      <SignUp
        setHeaderClass={setHeaderClass}
        setWrapperClass={setWrapperClass}
        setBypassAuthCheck={setBypassAuthCheck}
        setDisplayBackButton={setDisplayBackButton}
      />
    );
  };

  useEffect(() => {
    setBackButtonRoute(getBackButtonRoute());
  }, [pathname]);

  useEffect(() => {
    if (contentReady) {
      setSignUpRoute(getSignUpRoute());
    }
  }, [isSignUpFlowV2Active, isSignUpDisabled, contentReady]);

  return {
    headerClass,
    bypassAuthCheck,
    setHeaderClass,
    setBypassAuthCheck,
    wrapperClass,
    setWrapperClass,
    displayBackButton,
    setDisplayBackButton,
    backButtonRoute,
    checkout,
    signUpRoute,
    contentReady: contentReady ?? false,
    isLoading,
    hideCookies,
    awaitingNativeSession,
  };
};

export const CassetteAppWrapper = () => {
  const {
    headerClass,
    wrapperClass,
    bypassAuthCheck,
    displayBackButton,
    backButtonRoute,
    signUpRoute,
    contentReady,
    isLoading,
    hideCookies,
    awaitingNativeSession,
  } = useApp();
  if (!contentReady || isLoading || awaitingNativeSession) {
    return null;
  }
  return (
    <>
      <GoogleTagManagerScript />
      <SignedInContextProvider>
        <ShoppingCartContextProvider>
          <Routes>
            <Route path="launch" element={<LaunchApp />} />
            <Route path="clearscore" element={<Clearscore />} />
            <Route path="creditkarma" element={<CreditKarma />} />
            <Route path="totallymoney" element={<TotallyMoney />} />
            <Route path="monevo" element={<Monevo />} />
            <Route path="tuk" element={<Tuk />} />
            <Route path="moneysupermarket" element={<MoneySupermarket />} />
            <Route path="vanquis" element={<Vanquis />} />
            <Route path="creditec" element={<Creditec />} />
            <Route path="influencer/:slot" element={<Influencer />} />
            <Route
              element={
                <RequireAuth>
                  <ApolloProvider client={client}>
                    <Layout />
                  </ApolloProvider>
                </RequireAuth>
              }
            >
              <Route path="/settings/*" element={<Settings />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/dashboard" element={<Home />} />
              {/* Layout route, so the paywall covers every nested store page
                  rather than only the index. */}
              <Route path="/store" element={<RequireStoreAccess />}>
                <Route path="categories" element={<AllStoreCategories />} />
                <Route path="category/:slug" element={<ProductList />} />
                <Route path="product/:id/:slug" element={<ProductDetails />} />
                <Route path="orders/*" element={<Orders />} />
                <Route path="cart" element={<Cart />} />
                <Route index element={<Store />} />
              </Route>
              <Route path="/checkout/*" element={<Checkout />} />
              <Route path="/ourimpact/*" element={<OurImpact />} />
              <Route
                path="/video"
                element={<VideoList playlistId={process.env.REACT_APP_YOUTUBE_PLAYLIST_ID ?? ""} />}
              />
              <Route path="/paymentError" element={<PaymentFailed />} />
              <Route path="/statements" element={<PeriodicStatements />} />
              {/* Deliberately outside RequireStoreAccess. These two are the
                  pages that explain a failure — the cart could not be loaded,
                  the cart expired — and are where the app sends someone when
                  it happens. Redirecting away from them would take the
                  explanation with it and leave an unexplained bounce to the
                  dashboard. They are terminal messages, not shopping. */}
              <Route path="/store/cart/error" element={<ShoppingCartFailure />} />
              <Route path="/store/timeout" element={<CartTimeout />} />
            </Route>
            <Route
              element={
                <RequireAuth>
                  <SignupLikeRoutesLayout
                    headerClass={headerClass}
                    wrapperClass={wrapperClass}
                    displayBackButton={displayBackButton}
                    backButtonRoute={backButtonRoute}
                  ></SignupLikeRoutesLayout>
                </RequireAuth>
              }
            >
              <Route path="/score-apply/*" element={<ScoreApply />} />
            </Route>
            <Route
              path="/"
              element={
                <UnauthorizedRoutesLayout
                  headerClass={headerClass}
                  wrapperClass={wrapperClass}
                  bypassAuthCheck={bypassAuthCheck}
                  displayBackButton={displayBackButton}
                  backButtonRoute={backButtonRoute}
                />
              }
            >
              <Route
                path="login"
                element={LOGIN_REDIRECT_ENABLED ? <ExternalRedirect to="https://www.getbits.app/login" /> : <Login />}
              />
              <Route path="smsverification" element={<MFA />} />
              <Route path="forgotPassword" element={<ResetPassword />} />
              <Route path="changepassword" element={<ChangePassword />} />
              {/* The public entry point only. There is one signup and it lives
                  on the landing site, so anyone arriving at /signup is sent
                  there — `ExternalRedirect` carries the query string over, so
                  aggregator and UTM params survive the hop.

                  The deeper /signup/* routes deliberately stay in the app.
                  They are not a second signup: they are where an already
                  authenticated user finishes setting up. `UnauthorizedRoutesLayout`
                  sends a signed-in user whose subscription is not yet active to
                  /signup/aboutyourdata, and /signup/confirmation, /signup/schedule
                  and /signup/paymentdate are post-payment pages. Redirecting the
                  whole subtree threw those off-site, which turned a visit to
                  /login into a one-way trip to the landing page's signup. */}
              {/* A signed-in user is never ejected to the marketing signup, no
                  matter what navigated here. Leaving this unconditional meant
                  any internal hop that landed on /signup threw an authenticated
                  user out of the app entirely, with no way back. Signed-out
                  visitors still get the redirect. */}
              <Route
                path="signup"
                element={
                  AuthService.isAuthenticated() ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <ExternalRedirect to={SIGNUP_SITE_URL} />
                  )
                }
              />
              <Route path="signup/*" element={signUpRoute} />
              <Route path="bits-discount/plans/:token/*" element={<SignUpDiscount />} />
              {/* The store's front door is sign-in, not sign-up. It has to make
                  the signed-in decision itself: /login no longer redirects an
                  authenticated visitor away, so leaving this pointing there
                  unconditionally would show the sign-in form to someone who is
                  already signed in. */}
              <Route
                index
                element={<Navigate to={AuthService.isAuthenticated() ? "/dashboard" : "/login"} replace />}
              />
              {!AuthService.isAuthenticated() && <Route path="*" element={<NotFound />} />}
            </Route>
            <Route path="logout" element={<Logout />} />
            <Route
              path="*"
              element={
                <ApolloProvider client={client}>
                  <Error404 />
                </ApolloProvider>
              }
            />
          </Routes>
        </ShoppingCartContextProvider>
        <TrackAnalytic />
      </SignedInContextProvider>
      {!hideCookies && <UserCookieConsent />}
    </>
  );
};
